/**
 * In-app dice roller (task 20.3, R2.2 / R2.3).
 *
 * "Tap to roll" calls the dice endpoint; the server rolls via the shared
 * true-RNG, records the roll in the campaign chat audit trail, and returns the
 * result, which this component displays and hands up via {@link onResult} so the
 * composer can carry a short tag (R2.2/R2.3).
 *
 * NOTE: an earlier design also offered "report a physical result" (the player
 * types the integer they rolled on a real die). That was removed: the server
 * never consumed the reported total — it always re-rolled via the true-RNG — so
 * the control silently overrode the player's die, and a self-reported roll that
 * is quietly less trusted than a tapped one is a trust-eroding UX. A player who
 * wants to narrate a physical roll can simply type it into the chat.
 *
 * Request shaping is delegated to the pure {@link buildDiceRequest} so it is
 * unit-tested outside React.
 */

import { useState } from "react";
import { buildDiceRequest, type RollContext } from "../dice.js";
import type { DiceRollRequest, DiceRollResponse } from "../api-client.js";

export interface DiceRollerProps {
  readonly context: RollContext;
  /** Submit a shaped request to the dice endpoint; returns the resolved roll. */
  readonly onRoll: (req: DiceRollRequest) => Promise<DiceRollResponse>;
  /**
   * OPTIONAL: notified AFTER a roll resolves, with the shaped request and the
   * server's result. The play screen uses this to append a short roll tag (e.g.
   * `[d20 roll of 5]`) to the chat composer so the player can add their own
   * words and send on their own terms — the roll itself is already in the audit
   * log regardless (R2.3). Omitted in isolation tests that only assert the
   * endpoint call.
   */
  readonly onResult?: (req: DiceRollRequest, res: DiceRollResponse) => void;
}

export function DiceRoller({ context, onRoll, onResult }: DiceRollerProps) {
  const [result, setResult] = useState<DiceRollResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const roll = async (): Promise<void> => {
    setError(null);
    setBusy(true);
    try {
      const req = buildDiceRequest(context);
      const res = await onRoll(req);
      setResult(res);
      // Hand the resolved roll up so the composer can carry a human-readable tag
      // (the roll is already recorded server-side; this is just a breadcrumb).
      onResult?.(req, res);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Roll failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="dice-roller" aria-label="Dice roller">
      <h2>
        Roll {context.dice}
        {context.modifier ? ` (+${context.modifier})` : ""}
        {context.dc != null ? ` vs DC ${context.dc}` : ""}
      </h2>

      <div className="dice-choices">
        <button
          type="button"
          className="tap-to-roll"
          disabled={busy}
          onClick={() => void roll()}
        >
          Tap to roll
        </button>
      </div>

      {result ? (
        <p className="dice-result" data-testid="dice-result" role="status">
          {result.auditText || `= ${result.total}`}
        </p>
      ) : null}
      {error ? (
        <p className="dice-error" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
}
